import React, { useState, useEffect, useMemo } from "react";
import { useTableColumnFilters, FilterTh } from "./tableFilters";
import { WorkshopCostItem, CostItem, CostCategory } from "../types";
import { costService } from "../services/dataService";
import { formatCurrency, turkishIncludes } from "../utils/formatters";
import AlertModal from "./AlertModal";
import ConfirmModal from "./ConfirmModal";
import "./WorkshopCostsModal.css";

interface Props {
  workshopId: string;
  workshopName: string;
  isOpen: boolean;
  onClose: () => void;
}

const WorkshopCostsModal: React.FC<Props> = ({
  workshopId,
  workshopName,
  isOpen,
  onClose,
}) => {
  const [workshopCosts, setWorkshopCosts] = useState<WorkshopCostItem[]>([]);
  const [availableCostItems, setAvailableCostItems] = useState<CostItem[]>([]);
  const [categories, setCategories] = useState<CostCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<WorkshopCostItem | null>(null);
  const [formData, setFormData] = useState({ costItemId: "", notes: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "warning" | "danger";
    onConfirm: () => void;
  }>({ isOpen: false, message: "", type: "warning", onConfirm: () => {} });

  useEffect(() => {
    if (isOpen && workshopId) {
      loadWorkshopCosts();
      loadAvailableCostItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, workshopId]);

  const loadWorkshopCosts = async () => {
    if (!workshopId) return;
    try {
      setLoading(true);
      const data = await costService.getWorkshopCostItems(workshopId);
      setWorkshopCosts(data);
    } catch (error) {
      console.error("❌ Failed to load workshop costs:", error);
      setAlertModal({
        isOpen: true,
        message: "Atölye maliyetleri yüklenemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableCostItems = async () => {
    try {
      const [itemsData, categoriesData] = await Promise.all([
        costService.getCostItems(),
        costService.getCostCategories(),
      ]);
      setAvailableCostItems(itemsData);
      setCategories(categoriesData);
    } catch (error) {
      console.error("❌ Failed to load cost items:", error);
    }
  };

  const openAddModal = () => {
    setEditingItem(null);
    setFormData({ costItemId: "", notes: "" });
    setSearchTerm("");
    setSelectedCategory("");
    setIsAddModalOpen(true);
  };

  const openEditModal = (item: WorkshopCostItem) => {
    setEditingItem(item);
    setFormData({ costItemId: item.costItemId, notes: item.notes || "" });
    setSearchTerm("");
    setSelectedCategory("");
    setIsAddModalOpen(true);
  };

  const closeAddModal = () => {
    setIsAddModalOpen(false);
    setEditingItem(null);
    setFormData({ costItemId: "", notes: "" });
    setSearchTerm("");
    setSelectedCategory("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!workshopId) return;

    if (!formData.costItemId || formData.costItemId.trim() === "") {
      setAlertModal({
        isOpen: true,
        message: "Maliyet kalemi seçimi zorunludur!",
        type: "warning",
      });
      return;
    }

    try {
      setLoading(true);
      const itemData = {
        workshopId,
        costItemId: formData.costItemId,
        isPreferred: false,
        notes: formData.notes.trim() || undefined,
        isActive: true,
      };

      if (editingItem) {
        await costService.updateWorkshopItem(
          editingItem.workshopCostItemId,
          itemData,
        );
        setAlertModal({
          isOpen: true,
          message: "Maliyet ilişkisi başarıyla güncellendi!",
          type: "success",
        });
      } else {
        await costService.createWorkshopItem(itemData);
        setAlertModal({
          isOpen: true,
          message: "Maliyet kalemi başarıyla eklendi!",
          type: "success",
        });
      }

      closeAddModal();
      loadWorkshopCosts();
    } catch (error: any) {
      console.error("❌ Failed to save workshop cost:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "İşlem başarısız oldu!";
      setAlertModal({ isOpen: true, message: errorMessage, type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (item: WorkshopCostItem) => {
    setConfirmModal({
      isOpen: true,
      message: `"${item.costItem?.itemName || item.costItemName}" maliyet kalemini bu atölyeden kaldırmak istediğinizden emin misiniz?`,
      type: "danger",
      onConfirm: async () => {
        try {
          setLoading(true);
          await costService.deleteWorkshopItem(item.workshopCostItemId);
          setAlertModal({
            isOpen: true,
            message: "Maliyet kalemi başarıyla kaldırıldı!",
            type: "success",
          });
          loadWorkshopCosts();
        } catch (error: any) {
          console.error("❌ Failed to delete workshop cost:", error);
          setAlertModal({
            isOpen: true,
            message: "Maliyet kalemi kaldırılamadı!",
            type: "error",
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const wcColDefs = useMemo(
    () => [
      { key: "item", value: (i: any) => i.costItemName || "" },
      { key: "category", value: (i: any) => i.categoryName || "" },
      { key: "unit", value: (i: any) => i.unitName || i.unitCode || "" },
      { key: "standard", type: "decimal", value: (i: any) => i.standardPrice ?? null },
      { key: "wprice", type: "decimal", value: (i: any) => i.workshopSpecificPrice ?? null },
      { key: "effective", type: "decimal", value: (i: any) => i.effectivePrice ?? null },
      { key: "preferred", value: (i: any) => (i.isPreferred ? "Tercih" : "Hayır") },
      { key: "notes", value: (i: any) => i.notes || "" },
    ],
    [],
  );
  const { ctx: wcCf, filtered: displayWorkshopCosts } = useTableColumnFilters(
    workshopCosts,
    wcColDefs,
  );

  if (!isOpen) return null;

  const matches = (item: CostItem) => {
    if (selectedCategory && item.costCategoryId !== selectedCategory) return false;
    if (searchTerm) {
      return (
        turkishIncludes(item.itemName, searchTerm) ||
        turkishIncludes(item.costCategory?.categoryName || "", searchTerm) ||
        turkishIncludes(item.description || "", searchTerm)
      );
    }
    return true;
  };
  const listItems = availableCostItems.filter(
    (item) => formData.costItemId === item.costItemId || matches(item),
  );
  const countItems = availableCostItems.filter(matches).length;

  return (
    <>
      <div className="wcm-overlay" onMouseDown={onClose}>
        <div className="wcm-content" onMouseDown={(e) => e.stopPropagation()}>
          <div className="wcm-header">
            <div className="ws-modal-title">
              <span className="ws-modal-icon">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7v10M9.5 9.3c0-1 1.1-1.8 2.5-1.8s2.5.7 2.5 1.8-1 1.5-2.5 1.9-2.5.8-2.5 1.9 1.1 1.6 2.5 1.6 2.5-.7 2.5-1.6" />
                </svg>
              </span>
              <div className="wcm-titletext">
                <h2>{workshopName || "Atölye"} — Maliyet Yönetimi</h2>
                <p>{workshopCosts.length} maliyet kalemi</p>
              </div>
            </div>
            <div className="wcm-header-actions">
              <button className="btn btn-primary" onClick={openAddModal}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                Maliyet Kalemi Ekle
              </button>
              <button className="workshops-modal-close" onClick={onClose} aria-label="Kapat">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
          </div>

          <div className="wcm-body">
            {loading && workshopCosts.length === 0 ? (
              <div className="wcm-loading">
                <div className="wcm-spinner" />
                <p>Maliyet kalemleri yükleniyor...</p>
              </div>
            ) : workshopCosts.length === 0 ? (
              <div className="workshop-costs-empty">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 8v8M9.5 10c0-.9 1.1-1.5 2.5-1.5s2.5.6 2.5 1.5-1 1.3-2.5 1.6-2.5.7-2.5 1.6 1.1 1.4 2.5 1.4 2.5-.6 2.5-1.4" />
                </svg>
                <h3>Henüz maliyet kalemi eklenmemiş</h3>
                <p>Bu atölyeye maliyet kalemi ekleyerek başlayın.</p>
              </div>
            ) : (
              <div className="workshop-costs-table-container wcm-table-scroll">
                <table className="workshop-costs-table">
                  <thead>
                    <tr>
                      <FilterTh label="Maliyet Kalemi" colKey="item" ctx={wcCf} />
                      <FilterTh label="Kategori" colKey="category" ctx={wcCf} />
                      <FilterTh label="Birim" colKey="unit" ctx={wcCf} />
                      <FilterTh label="Standart Fiyat" colKey="standard" type="decimal" ctx={wcCf} align="left" />
                      <FilterTh label="Atölye Fiyatı" colKey="wprice" type="decimal" ctx={wcCf} align="left" />
                      <FilterTh label="Efektif Fiyat" colKey="effective" type="decimal" ctx={wcCf} align="left" />
                      <FilterTh label="Tercih" colKey="preferred" ctx={wcCf} />
                      <FilterTh label="Not" colKey="notes" ctx={wcCf} />
                      <th>İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayWorkshopCosts.map((item) => (
                      <tr key={item.workshopCostItemId}>
                        <td>
                          <div className="item-name">
                            <strong>{item.costItemName}</strong>
                            {item.costItemDescription && (
                              <span className="item-description">
                                {item.costItemDescription}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <span className="category-badge">{item.categoryName}</span>
                        </td>
                        <td>
                          {item.unitName ? (
                            <span>
                              {item.unitName}
                              <span style={{ marginLeft: "4px", color: "#64748b", fontSize: "0.9em" }}>
                                ({item.unitCode})
                              </span>
                            </span>
                          ) : (
                            <span style={{ color: "#94a3b8" }}>{item.unitCode || "-"}</span>
                          )}
                        </td>
                        <td className="price-cell">
                          {item.standardPrice !== undefined
                            ? formatCurrency(item.standardPrice, item.standardCurrency)
                            : "-"}
                        </td>
                        <td className="price-cell">
                          {item.workshopSpecificPrice ? (
                            <span className="workshop-price">
                              {formatCurrency(item.workshopSpecificPrice, item.workshopCurrency)}
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="price-cell">
                          <span className="effective-price">
                            {item.effectivePrice !== undefined
                              ? formatCurrency(item.effectivePrice, item.effectiveCurrency)
                              : "-"}
                          </span>
                        </td>
                        <td>
                          {item.isPreferred && (
                            <span className="preferred-badge">
                              <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor"><path d="M12 2l2.9 6.3 6.9.7-5.1 4.6 1.4 6.8L12 17.8 6 20.4l1.4-6.8L2.3 9l6.9-.7L12 2z" /></svg>
                              Tercih
                            </span>
                          )}
                        </td>
                        <td>
                          {item.notes && (
                            <span className="notes-cell" title={item.notes}>
                              {item.notes.substring(0, 30)}
                              {item.notes.length > 30 ? "..." : ""}
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button className="btn btn-sm btn-secondary" onClick={() => openEditModal(item)} title="Düzenle">
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" /></svg>
                            </button>
                            <button className="btn btn-sm btn-soft-danger" onClick={() => handleDelete(item)} title="Kaldır">
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Ekle / Düzenle alt-modalı */}
      {isAddModalOpen && (
        <div className="workshops-modal-overlay" onMouseDown={closeAddModal}>
          <div className="workshops-modal-content" onMouseDown={(e) => e.stopPropagation()}>
            <div className="workshops-modal-header">
              <div className="ws-modal-title">
                <span className="ws-modal-icon">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 7v10M9.5 9.3c0-1 1.1-1.8 2.5-1.8s2.5.7 2.5 1.8-1 1.5-2.5 1.9-2.5.8-2.5 1.9 1.1 1.6 2.5 1.6 2.5-.7 2.5-1.6" />
                  </svg>
                </span>
                <h2>{editingItem ? "Maliyet Kalemini Düzenle" : "Yeni Maliyet Kalemi Ekle"}</h2>
              </div>
              <button className="workshops-modal-close" onClick={closeAddModal} aria-label="Kapat">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="workshops-modal-form">
              <div className="workshops-form-group">
                <label>Maliyet Kalemi *</label>
                <div className="wcm-ss">
                  <div className="wcm-ss-filters">
                    <div className="wcm-ss-search">
                      <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Kalem ara..."
                        disabled={!!editingItem}
                      />
                    </div>
                    <select
                      className="wcm-ss-cat"
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      disabled={!!editingItem}
                    >
                      <option value="">Tüm Kategoriler</option>
                      {categories.map((cat) => (
                        <option key={cat.costCategoryId} value={cat.costCategoryId}>
                          {cat.categoryName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <select
                    className="wcm-ss-list"
                    value={formData.costItemId}
                    onChange={(e) => setFormData({ ...formData, costItemId: e.target.value })}
                    required
                    disabled={!!editingItem}
                    size={6}
                  >
                    <option value="" disabled>
                      -- Maliyet kalemi seçin --
                    </option>
                    {listItems.map((item) => (
                      <option key={item.costItemId} value={item.costItemId}>
                        {item.itemName} - {item.costCategory?.categoryName} (
                        {formatCurrency(item.unitPrice, item.currency)}/{item.unit})
                      </option>
                    ))}
                  </select>
                  <span className="wcm-ss-count">{countItems} kalem listeleniyor</span>
                </div>
              </div>

              <div className="workshops-form-group">
                <label>Notlar</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Ek bilgiler veya notlar"
                  rows={3}
                />
              </div>

              <div className="workshops-modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeAddModal} disabled={loading}>
                  İptal
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? "Kaydediliyor..." : "Kaydet"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={() => {
          confirmModal.onConfirm();
          setConfirmModal({ ...confirmModal, isOpen: false });
        }}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />
    </>
  );
};

export default WorkshopCostsModal;
